// Third party
import React, { Component } from 'react';
import classNames from 'classnames';

// Components
import { PrimaryButton, PillButton, SecondaryLink } from '../buttons';
import { Filter, SearchInput } from '../inputs';
import { RoundedPastille } from '../misc';
import { withErrorBoundary } from '../errors';

// Utils
import { formatMessage, FormattedMessage } from '../../../utils/i18n';
import { Tooltip } from '../tooltips';
import Dictionary from '@/types/dictionary';
import SearchOptions from '@/types/search-options';
import FilterType from '@/types/filter-type';
import FilterOption from '@/types/filter-option';

interface Props {
  className?: string;
  onSearch: (options: SearchOptions) => void;
  clearPreLoadQuery: (query: string) => void;
  filters: FilterType[];
  loadingFilters?: boolean;
  preLoadQuery?: string;
  applySearchText: string;
  placeholderText: string;
  minimizedFiltersToShow?: number;
  loadingResults?: boolean;
}

interface State {
  activeFilters: Dictionary<any>;
  filtersToShow: number;
  expanded: boolean;
}

class AdvancedSearchInput extends Component<Props, State> {
  static defaultProps = {
    filters: [],
  };

  private filters: Filter[] = [];

  constructor(props: Props) {
    super(props);
    this.state = {
      activeFilters: {
        query: props.preLoadQuery || '',
      },
      filtersToShow: props.minimizedFiltersToShow || props.filters.length,
      expanded: !props.minimizedFiltersToShow,
    };
    if (props.preLoadQuery) {
      this.applySearch();
    }
  }

  formatFiltersForQuery = (filters: Dictionary<any>) =>
    Object.keys(filters).reduce((acc: Dictionary<any>, key) => {
      if (filters[key].hasOwnProperty('min') || filters[key].hasOwnProperty('max')) {
        if (filters[key].min != null) {
          acc[`min${key}`] = filters[key].min;
        }
        if (filters[key].max != null) {
          acc[`max${key}`] = filters[key].max;
        }
      } else {
        acc[key] = filters[key];
      }
      return acc;
    }, {});

  applySearch = (): void => {
    const filters = this.formatFiltersForQuery(this.state.activeFilters);
    this.props.onSearch(filters);
  };

  clearAllFilters = (): void => {
    this.filters.forEach(filter => filter.clearFilter());
    this.onSearchInputChange('');
    this.setState({ activeFilters: {} });
  };

  setFilterValue = (options: FilterOption[], filter: FilterType) => {
    const { activeFilters } = this.state;

    delete activeFilters[filter.key];

    if (options.length > 0) {
      if (filter.type === 'range') {
        activeFilters[filter.key] = {
          min: options[0].value[0],
          max: options[0].value[1],
        };
      } else {
        activeFilters[filter.key] = filter.multiselect ? options.map(o => o.value) : options[0].value;
      }
    }
    this.setState({ activeFilters });
  };

  onSearchInputChange = (query: string) => {
    if (this.props.preLoadQuery) {
      this.props.clearPreLoadQuery('');
    }
    const { activeFilters } = this.state;
    activeFilters.query = query;
    this.setState({ activeFilters });
  };

  setFilterRef = (filter: Filter) => {
    this.filters.push(filter);
  };

  getFiltersCount = () => {
    const { activeFilters } = this.state;
    return Object.keys(activeFilters).filter(f => activeFilters[f] !== '').length;
  };

  showMore = () => {
    const { filters, minimizedFiltersToShow } = this.props;

    if (minimizedFiltersToShow === undefined) {
      return;
    }

    this.setState(prevState => ({
      ...prevState,
      expanded: !prevState.expanded,
      filtersToShow: prevState.expanded ? minimizedFiltersToShow : filters.length,
    }));
  };

  render() {
    const { filters, className, applySearchText, placeholderText, minimizedFiltersToShow, loadingFilters, loadingResults } = this.props;
    const { filtersToShow, expanded } = this.state;

    const noActiveFilter = this.getFiltersCount() === 0;

    return (
      <div className={classNames(className, 'advanced-search-input')}>
        <div className="d-flex search-filters-input">
          <SearchInput
            onChange={this.onSearchInputChange}
            onSubmit={this.applySearch}
            className="flex-grow-1"
            placeholder={placeholderText}
            value={this.state.activeFilters.query || this.props.preLoadQuery}
          />
          <PrimaryButton onClick={this.applySearch} loading={loadingResults}>
            {applySearchText}
          </PrimaryButton>
        </div>
        <div className="d-flex flex-wrap search-filters">
          {filters.map((filter, i) => (
            <Filter
              key={i}
              ref={this.setFilterRef}
              onChange={value => {
                this.setFilterValue(value, filter);
              }}
              multiselect={filter.multiselect}
              search={filter.search}
              optionsGroups={[
                {
                  options: filter.options,
                },
              ]}
              loading={loadingFilters}
              tooltip={filter.tooltip}
              label={filter.label}
              className={classNames({ hidden: i + 1 > filtersToShow && minimizedFiltersToShow !== undefined })}
            />
          ))}

          {minimizedFiltersToShow !== undefined && expanded && (
            <Tooltip message={formatMessage('AdvancedSearch.Filters.ShowLessTooltip')}>
              <PillButton onClick={this.showMore}>
                <FormattedMessage id="AdvancedSearch.Filters.ShowLess" />
              </PillButton>
            </Tooltip>
          )}

          {minimizedFiltersToShow !== undefined && !expanded && (
            <Tooltip message={formatMessage('AdvancedSearch.Filters.ShowMoreTooltip')}>
              <PillButton onClick={this.showMore}>
                <FormattedMessage id="AdvancedSearch.Filters.ShowMore" />
              </PillButton>
            </Tooltip>
          )}
        </div>

        <div className="filters-lower-section d-flex justify-content-end">
          <div className={classNames('filters-actions', { 'no-active-filter': noActiveFilter })}>
            <SecondaryLink noBorder onClick={this.clearAllFilters} disabled={noActiveFilter}>
              <FormattedMessage id="Search.ClearFilters" />
            </SecondaryLink>
            <RoundedPastille>{this.getFiltersCount()}</RoundedPastille>
          </div>
        </div>
      </div>
    );
  }
}

export default withErrorBoundary(AdvancedSearchInput);
